import { useState } from "react";
import MarketingLayout from "@/components/layout/MarketingLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/contact", form);
      toast.success(r.data.message);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit");
    } finally { setLoading(false); }
  };

  return (
    <MarketingLayout>
      <section className="page-shell px-6 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">Contact</div>
            <h1 className="font-display text-5xl lg:text-6xl mt-3 leading-[1.05]">We're here to help.</h1>
            <p className="text-lg text-muted-foreground mt-6 max-w-md">Reach us via the form, live chat, WhatsApp or phone. Response within one business day.</p>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-primary" /> hello@finzpe.com</div>
            <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-primary" /> +91 87000 59442</div>
            <div className="flex items-center gap-3"><MapPin className="h-4 w-4 text-primary" /> Sector 63, Noida, Uttar Pradesh 201301, India</div>
          </div>
          <div className="rounded-3xl overflow-hidden border border-border">
            <iframe
              title="Finzpe HQ Map"
              src="https://www.google.com/maps?q=Noida+Sector+63,+Uttar+Pradesh&output=embed"
              className="w-full h-56 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </div>
        <Card className="lg:col-span-7 p-8 rounded-3xl">
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" value={form.name} onChange={set("name")} required data-testid="contact-name" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="contact-email" />
              </div>
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
            </div>
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" rows={6} value={form.message} onChange={set("message")} required data-testid="contact-message" />
            </div>
            <Button type="submit" disabled={loading} className="rounded-full brand-gradient text-white" data-testid="contact-submit">{loading ? "Sending..." : "Send message"}</Button>
          </form>
        </Card>
      </section>
    </MarketingLayout>
  );
}
